import React from 'react';
import { Link } from 'react-router-dom';
import { Check, X, Minus, ArrowRight, Library, Network, Compass, Code2, MousePointerClick, Search, Target, Bot } from 'lucide-react';
import Section from './Section';
import { CASE_STUDIES } from '../../data/caseStudies';

const Eyebrow = ({ children }) => <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">{children}</p>;
const H2 = ({ children, id }) => <h2 id={id} className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-2 max-w-3xl scroll-mt-28">{children}</h2>;

export const DefineSection = () => (
  <Section bg="white" testId="hub-define">
    <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-10 items-start">
      <div>
        <Eyebrow>The term explained</Eyebrow>
        <H2 id="what-is-an-authority-hub">What is an authority hub, and why do coaches need one?</H2>
        <p className="text-[#475569] mt-5 text-lg leading-relaxed">
          An <strong className="text-[#1E2A44]">authority hub</strong> is a business website that proves your expertise by bringing everything
          you have made into one place: podcast episodes, books, YouTube videos, articles and services, all interlinked on your own domain.
          It is the name we use at Audio &amp; Co. for the business websites we build.
        </p>
        <p className="text-[#475569] mt-4 leading-relaxed">
          Coaches, consultants and authors need one because buyers research before they book. They search your name, your topic and the
          questions they are stuck on. With a hub, every one of those searches lands on something you made, and every page leads to the next,
          until booking a call feels like the obvious step.
        </p>
      </div>
      <div className="rounded-[14px] bg-[#1E2A44] text-white p-7" data-testid="hub-define-example">
        <p className="text-[#E7A3C4] text-xs uppercase tracking-widest font-semibold">What lives in a hub</p>
        <ul className="mt-4 space-y-2.5 text-white/85 text-[15px]">
          <li>Every podcast episode, with show notes and transcript</li>
          <li>Long-form YouTube videos and Shorts</li>
          <li>SEO articles grouped into topic hubs</li>
          <li>Your books and audiobooks</li>
          <li>Services, case studies and a booking path</li>
          <li>An AI chatbot trained on your content</li>
        </ul>
      </div>
    </div>
  </Section>
);

const PARTS = [
  { icon: Library, t: 'A searchable content library', d: 'Every episode, video and article on your domain, readable, watchable and listenable.' },
  { icon: Network, t: 'Topic hubs and internal links', d: 'Content grouped by the problems your clients have, each page linking to the next best one.' },
  { icon: Compass, t: 'A start-here path', d: 'A guided route for first-time visitors, from their first question to your services.' },
  { icon: Code2, t: 'Structured data for search and AI', d: 'Schema, transcripts and speakable markup so Google, Siri and ChatGPT can understand and cite you.' },
  { icon: MousePointerClick, t: 'A clear conversion path', d: 'Book-a-call, chatbot and newsletter prompts placed where intent is highest.' },
];

export const FivePartsSection = () => (
  <Section bg="bg" testId="hub-five-parts">
    <Eyebrow>What makes it work</Eyebrow>
    <H2>The 5 essential parts of a high-converting business website</H2>
    <ol className="mt-10 grid md:grid-cols-2 lg:grid-cols-5 gap-4">
      {PARTS.map(({ icon: Icon, t, d }, i) => (
        <li key={t} className="bg-white border border-[#E2E8F0] rounded-[14px] p-6 transition-transform duration-300 hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <Icon className="w-6 h-6 text-[#BC516F]" />
            <span className="font-display font-bold text-[#E2E8F0] text-3xl">0{i + 1}</span>
          </div>
          <h3 className="font-display font-semibold text-[#1E2A44] text-lg mt-4 leading-snug">{t}</h3>
          <p className="text-[#475569] mt-2 text-[15px] leading-relaxed">{d}</p>
        </li>
      ))}
    </ol>
  </Section>
);

const BENEFITS = [
  { icon: Search, t: 'Ranks for expertise searches', d: 'Each article and episode page targets a real question your clients type into Google, so you show up while they are still researching.' },
  { icon: Target, t: 'Captures buying intent', d: 'Service pages, pricing and case studies catch the "hire someone" searches and turn them into booked calls.' },
  { icon: Bot, t: 'Gets cited by AI assistants', d: 'Clean structure, schema and transcripts make your content easy for ChatGPT, Perplexity and voice assistants to quote.' },
];

export const SeoBenefitsSection = () => (
  <Section bg="white" testId="hub-seo-benefits">
    <Eyebrow>Why search matters</Eyebrow>
    <H2>How an SEO-optimised business website attracts your ideal clients</H2>
    <p className="text-[#475569] mt-5 max-w-3xl leading-relaxed">
      Scattered content works against you: a podcast on Spotify, videos on YouTube and posts on social each send people somewhere else.
      A centralised website collects all of that attention on your domain, where Google can see your depth on a topic and visitors can
      binge their way to a call. If you are <Link to="/podcast-management#authority-podcasting" className="text-[#BC516F] hover:text-[#A7468A] font-semibold">looking to establish authority through podcasting</Link>, your website is where that authority is stored.
    </p>
    <div className="mt-10 grid md:grid-cols-3 gap-5">
      {BENEFITS.map(({ icon: Icon, t, d }) => (
        <div key={t} className="bg-[#F6F8FB] border border-[#E2E8F0] rounded-[14px] p-7">
          <span className="w-10 h-10 rounded-full bg-gradient-to-br from-[#A7468A] to-[#BC516F] flex items-center justify-center"><Icon className="w-5 h-5 text-white" /></span>
          <h3 className="font-display font-semibold text-[#1E2A44] text-xl mt-4">{t}</h3>
          <p className="text-[#475569] mt-2 leading-relaxed">{d}</p>
        </div>
      ))}
    </div>
  </Section>
);

const ROWS = [
  ['You own it', 'yes', 'yes', 'no'],
  ['Ranks on Google for your expertise', 'yes', 'part', 'no'],
  ['Easy for AI assistants to cite', 'yes', 'part', 'no'],
  ['Holds your podcast, books, videos and articles', 'yes', 'no', 'part'],
  ['Content keeps working for years', 'yes', 'part', 'no'],
  ['Guides visitors to book a call', 'yes', 'part', 'part'],
];
const MARK = {
  yes: <Check className="w-5 h-5 text-[#15803D] mx-auto" aria-label="Yes" />,
  part: <Minus className="w-5 h-5 text-[#B45309] mx-auto" aria-label="Partly" />,
  no: <X className="w-5 h-5 text-[#BE123C] mx-auto" aria-label="No" />,
};

export const ComparisonSection = () => (
  <Section bg="bg" testId="hub-comparison">
    <Eyebrow>Side by side</Eyebrow>
    <H2>Business website vs brochure website vs social profiles</H2>
    <div className="mt-10 overflow-x-auto rounded-[14px] border border-[#E2E8F0] bg-white">
      <table className="w-full min-w-[640px] text-left" data-testid="hub-comparison-table">
        <thead>
          <tr className="border-b border-[#E2E8F0]">
            <th className="p-4 text-sm font-semibold text-[#64748B]" scope="col">What you get</th>
            <th className="p-4 text-sm font-bold text-white bg-gradient-to-r from-[#A7468A] to-[#BC516F] text-center" scope="col">Business website</th>
            <th className="p-4 text-sm font-semibold text-[#1E2A44] text-center" scope="col">Brochure website</th>
            <th className="p-4 text-sm font-semibold text-[#1E2A44] text-center" scope="col">Social media profiles</th>
          </tr>
        </thead>
        <tbody>
          {ROWS.map(([label, ...vals]) => (
            <tr key={label} className="border-b border-[#E2E8F0] last:border-0">
              <th scope="row" className="p-4 text-[15px] font-medium text-[#1E2A44]">{label}</th>
              {vals.map((v, i) => <td key={i} className={`p-4 ${i === 0 ? 'bg-[#A7468A]/5' : ''}`}>{MARK[v]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <p className="mt-4 text-sm text-[#64748B]">Tick = yes, dash = partly, cross = no.<span className="sm:hidden"> Swipe the table to compare all three.</span></p>
  </Section>
);

export const ProofStrip = () => (
  <Section bg="white" innerClassName="!py-12" testId="hub-proof-strip">
    <div className="grid md:grid-cols-2 gap-5">
      {CASE_STUDIES.map((c) => (
        <Link key={c.slug} to={`/case-studies/${c.slug}`} className="group flex gap-5 items-center rounded-[14px] border border-[#E2E8F0] p-4 transition-shadow duration-300 hover:shadow-[0_18px_44px_rgba(30,42,68,0.10)]" data-testid={`hub-proof-${c.slug}`}>
          <img src={`/portfolio/${c.build.folder}/home.jpg`} alt={c.build.shots[0].alt} title={c.build.shots[0].title} width="160" height="100" loading="lazy" className="w-32 sm:w-40 aspect-[16/10] object-cover object-top rounded-lg shrink-0" />
          <div>
            <p className="text-xs uppercase tracking-widest text-[#BC516F] font-semibold">Case study</p>
            <p className="font-display font-semibold text-[#1E2A44] text-lg">{c.cardTitle}</p>
            <span className="text-sm text-[#BC516F] group-hover:text-[#A7468A] font-semibold inline-flex items-center gap-1">Podcast and website for {c.audience} <ArrowRight className="w-3.5 h-3.5" /></span>
          </div>
        </Link>
      ))}
    </div>
  </Section>
);

const STEPS = [
  { t: 'Free call and content audit', d: 'We look at everything you already have: episodes, videos, books, posts and services.' },
  { t: 'Structure', d: 'We map your topics, pages and start-here path around what your clients search for.' },
  { t: 'Build and bring it all in', d: 'We build the site on your domain, import your content and add schema and transcripts.' },
  { t: 'Launch and track', d: 'Google Search Console and Bing are connected from day one, so you can watch rankings grow.' },
];

export const ApproachSection = () => (
  <Section bg="bg" testId="hub-approach">
    <Eyebrow>How we build it</Eyebrow>
    <H2>Building your business website: the Audio &amp; Co. approach</H2>
    <ol className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {STEPS.map((s, i) => (
        <li key={s.t} className="bg-white rounded-[14px] border border-[#E2E8F0] p-6">
          <span className="font-display font-bold text-[#BC516F] text-sm">Step {i + 1}</span>
          <h3 className="font-display font-semibold text-[#1E2A44] text-lg mt-1">{s.t}</h3>
          <p className="text-[#475569] mt-1.5 text-[15px] leading-relaxed">{s.d}</p>
        </li>
      ))}
    </ol>
  </Section>
);

export const HUB_FAQS = [
  { question: 'What is an authority hub?', answer: 'An authority hub is a business website that brings your podcast, books, YouTube videos, articles and services together on your own domain, all interlinked and built to be found on Google and AI search. It is the name Audio & Co. uses for the business websites we build.' },
  { question: 'How is a business website different from a brochure website?', answer: 'A brochure website lists your services. A business website built as an authority hub also holds all of your content, grouped by topic, so it ranks for the questions your clients search and guides them towards booking a call.' },
  { question: 'How much does a business website cost?', answer: 'Standalone business website builds start from \u00a3495 one-off. Ongoing weekly website updates are included in the Pro Plan, which has a 4-week minimum and can be cancelled any time.' },
  { question: 'Will it help me get found on Google and ChatGPT?', answer: 'Yes, that is what it is built for. Every page has structured data, clear headings and internal links, and your episodes have transcripts, which helps Google rank you and helps AI assistants cite you.' },
  { question: 'Can you bring my existing podcast, videos and blog across?', answer: 'Yes. We import your episodes, videos and articles so the new site starts with everything you have already made.' },
];

export const HubFaqSection = () => (
  <Section bg="white" testId="hub-faq">
    <H2>Business website questions</H2>
    <div className="mt-8 space-y-4 max-w-3xl">
      {HUB_FAQS.map((f, i) => (
        <details key={f.question} className="group bg-[#F6F8FB] border border-[#E2E8F0] rounded-[14px] p-5" data-testid={`hub-faq-${i}`}>
          <summary className="cursor-pointer font-display font-semibold text-[#1E2A44] list-none flex justify-between gap-4">{f.question}<span className="text-[#BC516F] transition-transform duration-300 group-open:rotate-45">+</span></summary>
          <p className="text-[#475569] mt-3 leading-relaxed">{f.answer}</p>
        </details>
      ))}
    </div>
  </Section>
);
